import { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, Save, FileText, Upload, X, Loader2 } from 'lucide-react';
import { toast } from "sonner";

import type {
  TSelectedTool,
  TSelectedToolSave,
  TToolType,
  TToolArgumentEditor,
  ToolFile,
} from '@intone/shared/types';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useUpdateTool,
  useUploadToolFile,
  useDeleteToolFile,
} from '@/entities/tool';

import { ToolArgumentView } from './ToolArgumentView';
import { buildEditorArgs, packArguments, validateFields } from '../lib';
import { useUnsavedChanges } from './UnsavedChangesContext';

// RAG tool type IDs that should show file upload
const RAG_TOOL_TYPE_IDS = [15];

// Supported file types for RAG (OpenAI vector store compatible)
const SUPPORTED_FILE_EXTENSIONS = ['.pdf', '.txt', '.md', '.html', '.json', '.docx', '.pptx', '.csv'];
const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/html',
  'application/json',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Validate file for RAG upload
function validateRagFile(file: File): { valid: boolean; error?: string } {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!SUPPORTED_FILE_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Unsupported file type "${extension}". Supported formats: ${SUPPORTED_FILE_EXTENSIONS.join(', ')}`,
    };
  }

  if (file.type && !SUPPORTED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Unsupported file type "${file.type}". Please upload a supported document format.`,
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large (${sizeMB} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.`,
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty. Please select a file with content.',
    };
  }

  return { valid: true };
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Form state type - single source of truth for all editable fields
type FormState = {
  name: string;
  description: string;
  args: TToolArgumentEditor[];
  files: ToolFile[];
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  args: [],
  files: [],
};

// Build form state from server tool data
function buildFormState(tool: TSelectedTool, toolTypes: TToolType[]): FormState {
  const tt = toolTypes.find((t) => t.id === tool.tool_type_id);
  const required = tt?.required_arguments || [];

  return {
    name: tool.name,
    description: tool.description || '',
    args: buildEditorArgs(required, tool.arguments || {}),
    files: tool.files || [],
  };
}

// Check if form has changes compared to server state
function hasFormChanges(form: FormState, tool: TSelectedTool): boolean {
  if (form.name !== tool.name) return true;
  if (form.description !== (tool.description || '')) return true;

  const currentArgs = packArguments(form.args);
  const serverArgs = tool.arguments || {};
  const allKeys = new Set([...Object.keys(currentArgs), ...Object.keys(serverArgs)]);

  for (const key of allKeys) {
    const curr = currentArgs[key];
    const srv = serverArgs[key];
    if (!curr || !srv) return true;
    if (curr.value !== srv.value) return true;
    if (curr.valueType !== srv.valueType) return true;
  }

  return false;
}

type ToolEditorProps = {
  tool: TSelectedTool | null;
  toolTypes: TToolType[];
};

export function ToolEditor({ tool, toolTypes }: ToolEditorProps) {
  const { setHasUnsavedChanges } = useUnsavedChanges();

  const updateTool = useUpdateTool();
  const uploadFile = useUploadToolFile();
  const deleteFile = useDeleteToolFile();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedToolIdRef = useRef<number | null>(null);

  // Single form state for all editable fields
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isError, setIsError] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);

  // Derived values
  const toolTypeId = tool?.tool_type_id || null;
  const selectedToolType = toolTypes.find((tt) => tt.id === toolTypeId);
  const isRagTool = toolTypeId !== null && RAG_TOOL_TYPE_IDS.includes(toolTypeId);

  // Dirty check - compare form against server state
  const hasChanges = useMemo(() => {
    if (!tool) return false;
    return hasFormChanges(form, tool);
  }, [form, tool]);

  // Sync form from server when tool changes
  useEffect(() => {
    if (tool && tool.id !== loadedToolIdRef.current) {
      loadedToolIdRef.current = tool.id;
      setForm(buildFormState(tool, toolTypes));
      setIsError(false);
    }
  }, [tool, toolTypes]);

  // Sync unsaved changes to context
  useEffect(() => {
    setHasUnsavedChanges(hasChanges);
  }, [hasChanges, setHasUnsavedChanges]);

  // Form update helpers
  const updateForm = (patch: Partial<FormState>) => {
    setForm(prev => ({ ...prev, ...patch }));
  };

  const updateArg = (id: string, patch: Partial<TToolArgumentEditor>) => {
    setForm(prev => ({
      ...prev,
      args: prev.args.map(a => a.id === id ? { ...a, ...patch } : a),
    }));
  };

  const addArgument = () => {
    if (!selectedToolType?.allow_args) return;
    const newArg: TToolArgumentEditor = {
      id: `new-${Date.now()}`,
      name: '',
      is_required: false,
      argType: undefined,
      valueType: 'fixed_value',
      value: '',
      fromRequired: false,
    };
    setForm(prev => ({ ...prev, args: [...prev.args, newArg] }));
  };

  const removeArgument = (id: string) => {
    setForm(prev => ({
      ...prev,
      args: prev.args.filter(a => a.id !== id || a.fromRequired),
    }));
  };

  // File handlers
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !tool?.id) return;

    event.target.value = '';

    const validation = validateRagFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Save first so chunking params are applied
    if (hasChanges) {
      handleSave();
    }

    uploadFile.mutate(
      { toolId: tool.id, file },
      {
        onSuccess: (result) => {
          toast.success(`File "${file.name}" uploaded successfully`);
          setForm(prev => ({
            ...prev,
            files: [
              ...prev.files,
              {
                id: result.file_id,
                filename: file.name,
                file_size: file.size,
                mime_type: file.type || 'application/octet-stream',
                openai_file_id: result.openai_file_id,
                created_at: new Date().toISOString(),
              },
            ],
          }));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to upload file');
        },
      }
    );
  };

  const handleDeleteFile = (fileId: number, filename: string) => {
    if (!tool?.id) return;

    setDeletingFileId(fileId);
    deleteFile.mutate(
      { toolId: tool.id, fileId },
      {
        onSuccess: () => {
          toast.success(`File "${filename}" deleted`);
          setDeletingFileId(null);
          setForm(prev => ({
            ...prev,
            files: prev.files.filter(f => f.id !== fileId),
          }));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to delete file');
          setDeletingFileId(null);
        },
      }
    );
  };

  // Save handler
  const handleSave = () => {
    if (!tool) return;

    const err = validateFields({ ...tool, name: form.name }, form.args);
    if (err) {
      setIsError(true);
      toast.error(err);
      return;
    }

    const payload: TSelectedToolSave = {
      name: form.name,
      description: form.description || null,
      tool_type_id: tool.tool_type_id!,
      arguments: packArguments(form.args),
    };

    updateTool.mutate({ ...payload, id: tool.id }, {
      onSuccess: (result) => {
        toast.success(result.message);
        setIsError(false);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to update tool');
      },
    });
  };

  const isSaving = updateTool.isPending;

  return (
    <div className="px-4 pb-4 space-y-3">
      {/* Tool Settings Card */}
      <Card className="border-gray-300">
        <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedToolType?.icon_url && (
                <img src={selectedToolType.icon_url} alt="" className="w-5 h-5" />
              )}
              <CardTitle className="text-body font-semibold">Tool Settings</CardTitle>
              {selectedToolType && (
                <span className="text-detail text-gray-500 font-normal">
                  {selectedToolType.type_name.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              size="sm"
            >
              <Save className="w-3 h-3 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {/* Name */}
          <div className="grid gap-1">
            <Label htmlFor="tool_name" className="font-medium text-gray-700">Name</Label>
            <Input
              id="tool_name"
              value={form.name}
              onChange={(e) => updateForm({ name: e.target.value })}
              placeholder="Tool name"
              className={`h-8 max-w-sm ${isError && !form.name.trim() ? 'border-destructive' : ''}`}
            />
          </div>

          {/* Description */}
          <div className="grid gap-1">
            <Label htmlFor="tool_description" className="font-medium text-gray-700">Description</Label>
            <Textarea
              id="tool_description"
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
              placeholder="Describe in detail when the LLM should call this tool"
              className="min-h-[60px]"
            />
          </div>

          {selectedToolType?.instructions && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="text-detail font-semibold text-blue-900 mb-1 block">
                Tool Instructions
              </div>
              <div className="text-detail text-blue-800 whitespace-pre-wrap">
                {selectedToolType.instructions}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* RAG Files */}
      {isRagTool && (
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-gray-50 py-2">
            <CardTitle className="text-body flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Knowledge Base Files
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 pb-3">
            <div className="space-y-3">
              {form.files.length > 0 ? (
                <div className="space-y-2">
                  {form.files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-detail font-medium truncate">{file.filename}</p>
                          <p className="text-detail text-gray-500">
                            {formatFileSize(file.file_size)}
                            {file.mime_type && ` • ${file.mime_type}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteFile(file.id, file.filename)}
                        disabled={deletingFileId !== null}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      >
                        {deletingFileId === file.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-detail">No files uploaded yet</p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                accept={SUPPORTED_FILE_EXTENSIONS.join(',')}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadFile.isPending}
                className="w-full"
              >
                {uploadFile.isPending ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-3 h-3 mr-2" />
                    Upload File
                  </>
                )}
              </Button>

              <p className="text-[10px] text-gray-400 text-center">
                Supported: PDF, TXT, MD, HTML, JSON, DOCX, PPTX, CSV (max {MAX_FILE_SIZE_MB} MB)
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arguments */}
      {!!(form.args.length || selectedToolType?.allow_args) && (
        <Card className="border-gray-300">
          <CardHeader className="bg-gray-50 border-b border-gray-300 py-2">
            <CardTitle className="text-body font-semibold">Arguments</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {form.args.map((arg) => (
              <ToolArgumentView
                key={arg.id}
                arg={arg}
                allowRename={!arg.fromRequired && !!selectedToolType?.allow_args}
                onChange={(patch) => updateArg(arg.id, patch)}
                onRemove={removeArgument}
                isError={isError}
              />
            ))}

            {selectedToolType?.allow_args && (
              <Button
                variant="outline"
                onClick={addArgument}
                className="w-full sm:w-auto"
              >
                <Plus className="w-3 h-3 mr-2" />
                Add Argument
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
